import { useState, useEffect, useRef } from 'react';

const BACKGROUNDS = [
  { id: 'none', name: 'None', type: 'none', preview: null },
  { id: 'blur', name: 'Blur', type: 'blur', preview: null },
  { id: 'office', name: 'Modern Office', type: 'image', url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&h=1080&fit=crop' },
  { id: 'home', name: 'Cozy Home', type: 'image', url: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1920&h=1080&fit=crop' },
  { id: 'nature', name: 'Nature', type: 'image', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&h=1080&fit=crop' },
  { id: 'abstract', name: 'Abstract', type: 'gradient', gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
  { id: 'gradient1', name: 'Sunset', type: 'gradient', gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
  { id: 'gradient2', name: 'Ocean', type: 'gradient', gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
  { id: 'gradient3', name: 'Forest', type: 'gradient', gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' },
];

export default function VirtualBackgrounds({ onSelectBackground, currentBackground, isOpen, onToggle }) {
  const [selected, setSelected] = useState(currentBackground || 'none');
  const [customImage, setCustomImage] = useState(null);
  const fileInputRef = useRef(null);

  const handleSelect = (background) => {
    setSelected(background.id);
    if (onSelectBackground) {
      onSelectBackground(background);
    }

    // Close picker after selection
    if (onToggle) {
      onToggle(false);
    }
  };

  const handleCustomImage = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const customBg = {
          id: 'custom',
          name: 'Custom',
          type: 'image',
          url: event.target.result
        };
        setCustomImage(customBg);
        handleSelect(customBg);
      };
      reader.readAsDataURL(file);
    }
  };

  const allBackgrounds = customImage ? [...BACKGROUNDS, customImage] : BACKGROUNDS;

  return (
    <div className="relative">
      <button
        onClick={() => onToggle && onToggle(!isOpen)}
        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center transition-all ${
          isOpen
            ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
            : 'bg-white/10 hover:bg-white/20 backdrop-blur-xl border border-white/10'
        }`}
        title="Virtual Background"
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute bottom-full right-0 mb-2 bg-[#1c1c1e] rounded-2xl p-6 shadow-2xl border border-[#2c2c2e] min-w-[500px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Virtual Background</h3>
            <button
              onClick={() => onToggle && onToggle(false)}
              className="p-1 hover:bg-[#2c2c2e] rounded transition"
            >
              <svg className="w-5 h-5 text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 max-h-96 overflow-y-auto">
            {allBackgrounds.map(bg => (
              <button
                key={bg.id}
                onClick={() => handleSelect(bg)}
                className={`relative aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                  selected === bg.id
                    ? 'border-[#06c] ring-2 ring-[#06c]/50'
                    : 'border-[#2c2c2e] hover:border-[#3c3c3e]'
                }`}
              >
                {bg.type === 'none' && (
                  <div className="w-full h-full bg-[#2c2c2e] flex items-center justify-center">
                    <svg className="w-8 h-8 text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </div>
                )}
                {bg.type === 'blur' && (
                  <div className="w-full h-full bg-gradient-to-br from-[#2c2c2e] to-[#3c3c3e] flex items-center justify-center backdrop-blur-2xl">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                )}
                {bg.type === 'image' && (
                  <img src={bg.url} alt={bg.name} className="w-full h-full object-cover" />
                )}
                {bg.type === 'gradient' && (
                  <div className="w-full h-full" style={{ background: bg.gradient }} />
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <div className="text-xs text-white font-medium">{bg.name}</div>
                </div>
                {selected === bg.id && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-[#06c] rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </button>
            ))}

            {/* Upload custom background */}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="relative aspect-video rounded-lg overflow-hidden border-2 border-dashed border-[#3c3c3e] hover:border-[#06c] transition-all bg-[#2c2c2e] flex items-center justify-center"
            >
              <div className="text-center">
                <svg className="w-8 h-8 text-[#8e8e93] mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <div className="text-xs text-[#8e8e93]">Upload</div>
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleCustomImage}
              className="hidden"
            />
          </div>

          <div className="mt-4 p-3 bg-[#2c2c2e] rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="text-xs text-[#8e8e93]">
                <strong className="text-green-400">AI-Powered:</strong> Uses real-time person segmentation to detect you and only blur/replace the background behind you. Works best with good lighting.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
